/**
 * Vraag de gebruiker of artikel-foto's moeten worden opgenomen in de PDF.
 *
 * Logica (V19 — altijd vragen):
 *  - De modal wordt ALTIJD getoond wanneer de PDF-generatie wordt getriggerd
 *  - De setting `pdf_toon_artikel_fotos` bepaalt enkel welke knop standaard actief is
 *  - Per-type filter (`pdf_fotos_types`) wordt NIET meer stil overgeslagen; user kiest zelf
 *  - Enige silent-skip: als de /instellingen call helemaal faalt (netwerkfout)
 *
 * Vroeger (V18-): modal werd stil overgeslagen bij `pdf_toon_artikel_fotos=false`.
 * Dit was verwarrend want preview en productie hadden verschillende defaults.
 *
 * @param {object} API axios instance
 * @param {string} [docType] bv. 'factuur'|'offerte'|'leverbon'|... — informatief, niet meer blokkerend
 */
import { iosConfirm } from './iosConfirm';

export async function vraagFotoKeuze(API, docType) {
  let standaardAan = false;
  try {
    const r = await API.get('/instellingen');
    standaardAan = !!r.data?.pdf_toon_artikel_fotos;
    // Diagnostic log — zichtbaar in F12 op productie
    console.info(`[foto-modal] tenant setting pdf_toon_artikel_fotos=${standaardAan}, docType=${docType || '(geen)'}`);
  } catch (err) {
    // Netwerkfout → val terug op "vraag maar" met "Nee" als default
    console.warn('[foto-modal] /instellingen lezen mislukt, toon toch modal:', err);
  }
  // iOS-stijl bevestiging — ALTIJD tonen zodat user consistent gedrag heeft
  // (voorheen werd modal stil overgeslagen bij pdf_toon_artikel_fotos=false,
  // wat verwarrend was omdat preview/productie verschillende defaults hadden)
  const met = await iosConfirm({
    titel: 'Artikel-foto\'s mee?',
    bericht: standaardAan
      ? 'Wil je deze PDF afdrukken met de foto\'s van de artikelen?'
      : 'Wil je deze PDF afdrukken met de foto\'s van de artikelen?\n\n(Instelling staat op "geen foto\'s" — bevestig uw keuze)',
    ja: 'Ja, met foto\'s',
    nee: 'Nee, zonder foto\'s',
    icon: '🖼️',
  });
  return met ? 1 : 0;
}

/** Helper: voeg de ?toon_fotos=X parameter toe aan een PDF-URL */
export function appendFotoParam(url, keuze) {
  if (keuze === null || keuze === undefined) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}toon_fotos=${keuze}`;
}

/**
 * Vraag of de PDF MET prijzen afgedrukt moet worden.
 * Wordt enkel gebruikt voor leverbon + bestelbon (andere types tonen altijd prijzen).
 * @param {string} docType bv. 'leverbon' of 'bestelbon'
 * @returns Promise<1|0|null>  — 1 = met prijzen, 0 = zonder, null = niet vragen (andere types)
 */
export async function vraagPrijsKeuze(docType) {
  if (docType !== 'leverbon' && docType !== 'bestelbon') return null;
  const label = docType === 'leverbon' ? 'leverbon' : 'bestelbon';
  const met = await iosConfirm({
    titel: `${label.charAt(0).toUpperCase() + label.slice(1)} mét prijzen?`,
    bericht: `Wil je dit ${label} afdrukken met de prijzen, BTW en totalen?`,
    ja: 'Ja, met prijzen',
    nee: 'Nee, zonder prijzen',
    icon: '💶',
  });
  return met ? 1 : 0;
}

/** Helper: voeg de ?met_prijzen=X parameter toe aan een PDF-URL */
export function appendPrijsParam(url, keuze) {
  if (keuze === null || keuze === undefined) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}met_prijzen=${keuze}`;
}
